import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OrderFormModal from '../modals/OrderFormModal';
import VendorSelectModal from '../modals/VendorSelectModal';
import { type Order, type CustomerData, type SelectedItem, type Vendor } from '../../types';

// 모달 컴포넌트들
import OrderDetailModal from '../orderModals/OrderDetailModal';
import ReverseOrderModal from '../orderModals/ReverseOrderModal';
import PaymentCompleteModal from '../orderModals/PaymentCompleteModal';
import TaxInvoiceModal from '../orderModals/TaxInvoiceModal';
import InvoiceModal from '../orderModals/InvoiceModal';

interface Props {
  customerId: string;
  customer: CustomerData;
}

const WEDDING_HALL_VENDOR_CATEGORY_IDX = 82;

type DownloadDocType = 'ORDER' | 'REVERSE' | 'INVOICE' | 'WEDDING_HALL' | 'TAX';

type DownloadDoc = {
  type: DownloadDocType;
  label: string;
};

const getOrderLifecycleStep = (status: string): number => {
  const raw = (status || '').trim();
  const normalized = raw.toUpperCase().replace(/\s+/g, '');

  if (raw === '거래완료' || normalized === 'COMPLETED' || normalized.includes('거래완료')) return 6;
  if (
    raw === '세금계산서' ||
    raw === '세금계산서발행완료' ||
    normalized === 'TAX_ISSUED' ||
    normalized.includes('세금계산서') ||
    normalized.includes('TAX')
  ) {
    return 5;
  }
  if (raw === '정산완료' || normalized === 'SETTLED' || normalized.includes('정산')) return 4;
  if (
    raw === '청구서도착' ||
    raw === '청구서발행완료' ||
    normalized === 'INVOICED' ||
    normalized.includes('INVOICE') ||
    normalized.includes('청구서')
  ) {
    return 3;
  }
  if (
    raw === '업체확인' ||
    raw === '역발주' ||
    normalized === 'REVERSE_ORDERED' ||
    normalized.includes('REVERSE') ||
    normalized.includes('업체확인')
  ) {
    return 2;
  }
  return 1;
};

const getResponseMessage = async (response: Response) => {
  const payload = await response.json().catch(() => null);
  return payload?.message || payload?.data?.message || '';
};

const normalizeBoolean = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    return lowered === 'y' || lowered === 'yes' || lowered === 'true' || lowered === '1';
  }
  return false;
};

const isLocalVendorCandidate = (vendor: Vendor | null) => {
  if (!vendor) return false;
  const contextType = String(vendor.contextType ?? '').trim().toUpperCase();
  if (contextType === 'PARTNER') return false;
  if (contextType === 'OWNER') return true;

  const relationshipType = String(vendor.relationshipType ?? '').trim().toUpperCase();
  if (relationshipType.includes('PARTNER')) return false;
  if (
    normalizeBoolean(vendor.isPartnerVendor) ||
    normalizeBoolean(vendor.isPartnershipVendor) ||
    normalizeBoolean(vendor.isGuestContext)
  ) {
    return false;
  }
  if (normalizeBoolean(vendor.isOwnerVendor)) return true;

  const vendorType = String(vendor.vendorType ?? '').trim().toUpperCase();
  return vendorType === 'LOCAL';
};

export default function OrdersTab({ customerId, customer }: Props) {
  const navigate = useNavigate();
  const [customerOrders, setCustomerOrders] = useState<Order[]>([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [showReverseOrderModal, setShowReverseOrderModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showTaxInvoiceModal, setShowTaxInvoiceModal] = useState(false);
  const [showPaymentCompleteModal, setShowPaymentCompleteModal] = useState(false);
  const [showVendorSelect, setShowVendorSelect] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  
  const [orderFormData, setOrderFormData] = useState({
     // ... 기존 초기값 유지
    orderDate: new Date().toISOString().split('T')[0],
    vendor: '', vendorManager: '', vendorPhone: '', vendorFax: '',
    companyName: '체리쉬웨딩', companyManager: customer.plannerName,
    companyPhone: '02-1234-5678', companyFax: '02-1234-5679',
    message: '아래와 같이 발주하오니 확인 후 진행 부탁드립니다.',
    shootingDate: '', shootingLocation: '', weddingDate: '', weddingLocation: '',
    notes: '상품 진행 1주일 전까지 고객 안내 및 방문 안내 부탁드립니다.',
    amount: '', commission: '', paymentTerms: '계약금 50% + 잔금 50%',
    dueDate: '', eventPlace: customer.weddingLocation || '', remark: '', serviceScope: '', specialNotes: ''
  });

  // ✅ [NEW] 드롭다운 메뉴 상태 관리
  const [activeDownloadMenuId, setActiveDownloadMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveDownloadMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const parseVendorCategoryIdx = (order: any): number | null => {
    const candidates = [
      order.vendorCategoryIdx,
      order.vendorCategoryId,
      order.partnerVendorCategoryIdx,
      order.vendorCategory,
      order.partnerVendor?.vendorCategoryIdx,
      order.partnerVendor?.category?.vendorCategoryIdx,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    // API에서 카테고리 ID가 없을 때, 이름 기반 최소한의 보정
    const categoryName = String(order.vendorCategoryName || order.vendorCategory || '').trim();
    if (categoryName === '웨딩홀') return WEDDING_HALL_VENDOR_CATEGORY_IDX;

    return null;
  };

  // ... (fetchOrders, handleAddOrder, handleSettlement, handleCancelOrder 등 기존 로직 유지) ...
   const fetchOrders = useCallback(async () => {
      // ... 기존 코드와 동일 ...
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/detail?tab=ORDERS`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.status && result.data && Array.isArray(result.data.tabData)) {
          setCustomerOrders(result.data.tabData.map((order: any) => ({
            id: order.orderIdx.toString(),
            orderIdx: order.orderIdx,
            orderNo: order.orderNo,
            customer: result.data.basicInfo.names,
            phone: result.data.basicInfo.groomContact,
            weddingDate: result.data.basicInfo.weddingDate || '',
            venue: result.data.basicInfo.weddingVenue,
            vendor: order.vendorName,
            vendorCategoryIdx: parseVendorCategoryIdx(order),
            paidAmount: order.paidAmount,
            manager: order.managerName,
            service: order.productName,
            amount: order.contractAmount.toString(),
            status: order.status,
            progress: order.progress,
            orderDate: new Date(order.orderDate).toLocaleDateString(),
            invoiceNumber: '', taxInvoiceNumber: '', notes: '', files: [],
            isExternalPartner: order.isExternalPartner === 'y',
          })));
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      }
    }, [customerId]);
  
    useEffect(() => {
      fetchOrders();
    }, [fetchOrders]);
  
    // ... handleCancelOrder, handleAddOrder, handleSettlement 등 기존 함수들 유지 ...
    const cancellableStatuses = [
        '발주완료', 'ORDERED',
        '업체확인', '역발주', 'REVERSE_ORDERED', 
        '청구서도착', 'INVOICED'
      ];
      const handleCancelOrder = async (orderIdx: string) => {
        if (!confirm('정말 발주를 취소하시겠습니까? (복구 불가)')) return;
        const reason = prompt('취소 사유를 입력해주세요. (선택사항)');
        if (reason === null) return; 
        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch(`https://api.wedmin.biz/order/${orderIdx}/cancel`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${token}` 
            },
            body: JSON.stringify({ reason: reason }) 
          });
          if (response.ok) {
            alert('발주가 정상적으로 취소되었습니다.');
            fetchOrders(); 
          } else {
            const err = await response.json();
            alert(`취소 실패: ${err.message}`);
          }
        } catch (e) {
          console.error(e);
          alert('서버 통신 중 오류가 발생했습니다.');
        }
      };

      const handleAddOrder = async (items: SelectedItem[], attachedContracts?: { id: string; vendorName: string; vendorCategory: string; productName: string; category: string }[]) => {
          if (!selectedVendor || items.length === 0) { alert('필수 항목을 선택해주세요.'); return; }
          const hasRehearsal = Boolean(customer.rehearsalDateTime && customer.rehearsalDateTime !== '-');
          const hasWedding = Boolean(customer.weddingDateTime && customer.weddingDateTime !== '-');
          if (!hasRehearsal || !hasWedding) {
            alert('발주서를 생성하려면 고객의 촬영일과 본식일이 모두 등록되어 있어야 합니다.');
            return;
          }
            try {
            const token = localStorage.getItem('accessToken');
            const totalAmount = Number(orderFormData.amount) || items.reduce((acc, item) => acc + item.price * item.quantity, 0);
            const trimmedRequiredDt = orderFormData.dueDate?.trim();
            const eventPlace = (orderFormData.eventPlace || customer.weddingLocation || '').trim();

            // 메모 + 첨부된 계약 현황 합치기
            let remark = (orderFormData.remark || orderFormData.specialNotes || '').trim();
            if (attachedContracts && attachedContracts.length > 0) {
              const contractLines = attachedContracts.map(
                (c) => `- [${c.vendorCategory}] ${c.vendorName}: ${c.productName}${c.category && c.category !== '-' ? ` (${c.category})` : ''}`
              ).join('\n');
              const contractSection = `\n\n[고객 계약 현황]\n${contractLines}`;
              remark = remark ? remark + contractSection : contractSection.trim();
            }
            const response = await fetch('https://api.wedmin.biz/order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                partnerVendorIdx: selectedVendor.vendorIdx,
                customerIdx: Number(customerId),
                orderedDt: orderFormData.orderDate,
                weddingDt: customer.weddingDateTime ? new Date(customer.weddingDateTime).toISOString() : '',
                ...(trimmedRequiredDt ? { requiredDt: trimmedRequiredDt } : {}),
                ...(eventPlace ? { eventPlace } : {}),
                ...(remark ? { remark } : {}),
                totalAmount,
                commissionRate: Number(orderFormData.commission),
                serviceScope: orderFormData.serviceScope,
                message: orderFormData.message,
                specialNote: orderFormData.specialNotes,
                items: items.map(item => ({ productIdx: item.productIdx, quantity: item.quantity }))
                })
            });
            if (response.ok) {
                alert('발주가 등록되었습니다.');
                setShowOrderForm(false);
                fetchOrders();
            } else {
                const message = await getResponseMessage(response);
                if (response.status === 403) {
                  alert(message || '권한이 없습니다.');
                  return;
                }
                alert(`발주 실패: ${message || '오류가 발생했습니다.'}`);
            }
            } catch (e) {
            console.error(e);
            alert('오류가 발생했습니다.');
            }
      }

      const handleSettlement = async (data: any) => {
        // ... 기존 코드 유지
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('https://api.wedmin.biz/order/settlement', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                orderIdx: selectedOrder?.id, // 실제 데이터 연동 필요
                amount: Number(data.amount),
                bankName: data.bankName,
                memo: data.memo,
                // sentDt must be a valid ISO 8601 date string
      
                sentDt:data.paidDate ? new Date(data.paidDate).toISOString() : '',
                // sentDt: new Date(data.paidDate).toISOString() // 'YYYY-MM-DD'
              })
            });
      
            if (response.ok) {
              alert('정산 처리가 완료되었습니다.');
              setShowPaymentCompleteModal(false);
              fetchOrders();
            } else {
              const err = await response.json();
              alert(`처리 실패: ${err.message}`);
            }
          } catch (e) {
            console.error(e);
            alert('오류가 발생했습니다.');
          }
      }

      const handleTransactionComplete = async (orderIdx: string) => {
        // ... 기존 코드 유지
        if(!confirm('해당 발주 건을 거래완료 처리하시겠습니까? 더 이상 수정할 수 없습니다.')) return;

        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch(`https://api.wedmin.biz/order/${orderIdx}/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          });
        } catch (e) {
          console.error(e);
          alert('오류가 발생했습니다.');
        }
        fetchOrders();
      }

      // 단방향 발주: 대리 역발주 확정
      const handleProxyConfirmOrder = async (data: any) => {
        if (!selectedOrder) return;
        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch(`https://api.wedmin.biz/order/confirm/${selectedOrder.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              confirmedPrice: Number(data.totalAmount) || 0,
              vendorMessage: data.notes || '',
              fileUrl: null,
            }),
          });
          if (response.ok) {
            alert('대리 역발주 확정이 완료되었습니다.');
            setShowReverseOrderModal(false);
            fetchOrders();
          } else {
            const err = await response.json();
            alert(`처리 실패: ${err.message}`);
          }
        } catch (e) {
          console.error(e);
          alert('오류가 발생했습니다.');
        }
      };

      // 단방향 발주: 대리 인보이스 등록
      const handleProxyInvoice = async (data: any) => {
        if (!selectedOrder) return;
        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch(`https://api.wedmin.biz/order/invoice/${selectedOrder.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              invoiceNo: data.invoiceNo || `INV-${Date.now()}`,
              totalAmount: Number(data.totalAmount) || 0,
              issueDt: data.issueDt || new Date().toISOString().split('T')[0],
              dueDt: data.dueDt || null,
              fileUrl: data.fileUrl || null,
            }),
          });
          if (response.ok) {
            alert('대리 인보이스 등록이 완료되었습니다.');
            setShowInvoiceModal(false);
            fetchOrders();
          } else {
            const err = await response.json();
            alert(`처리 실패: ${err.message}`);
          }
        } catch (e) {
          console.error(e);
          alert('오류가 발생했습니다.');
        }
      };

  // ✅ [수정됨] 3. PDF 다운로드 핸들러 (API 분기 처리)
  const handleDownloadPdf = async (orderIdx: string, type: DownloadDocType) => {
    setActiveDownloadMenuId(null); // 메뉴 닫기

    try {
      const token = localStorage.getItem('accessToken');
      let url = '';

      // 타입별 API 주소 분기
      if (type === 'INVOICE') {
        url = `https://api.wedmin.biz/order/${orderIdx}/invoice/download`;
      } else if (type === 'WEDDING_HALL') {
        url = `https://api.wedmin.biz/order/${orderIdx}/weddinghall/download`;
      } else if (type === 'REVERSE') {
        // ✅ 새로 만든 역발주 전용 API 연결
        url = `https://api.wedmin.biz/order/${orderIdx}/confirmation/download`;
      } else if (type === 'TAX') {
        url = `https://api.wedmin.biz/order/${orderIdx}/download?type=TAX`;
      } else {
        // 기본 발주서 (ORDER)
        url = `https://api.wedmin.biz/order/${orderIdx}/download?type=ORDER`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json(); 
        throw new Error(err.message || '다운로드 실패');
      }

      const blob = await response.blob(); 
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      const filePrefix = {
        ORDER: '발주서',
        REVERSE: '역발주확인서',
        INVOICE: '청구서',
        WEDDING_HALL: '웨딩홀확인서',
        TAX: '세금계산서'
      };
      
      link.setAttribute('download', `${filePrefix[type]}_${orderIdx}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

    } catch (e: any) {
      console.error(e);
      alert(`문서 다운로드 중 오류: ${e.message}`);
    }
  };

  // 상태/업체카테고리 기준 다운로드 문서 목록
  const getDownloadableDocs = (order: Order): DownloadDoc[] => {
    const docs: DownloadDoc[] = [{ type: 'ORDER', label: '발주서 (Order)' }];

    if (order.vendorCategoryIdx === WEDDING_HALL_VENDOR_CATEGORY_IDX) {
      docs.push({ type: 'WEDDING_HALL', label: '웨딩홀 확인서' });
    }

    const lifecycleStep = getOrderLifecycleStep(order.status);
    if (lifecycleStep >= 2) {
      docs.push({ type: 'REVERSE', label: '역발주확인서 (Confirm)' });
    }
    if (lifecycleStep >= 3) {
      docs.push({ type: 'INVOICE', label: '청구서 (Invoice)' });
    }
    if (lifecycleStep >= 5) {
      docs.push({ type: 'TAX', label: '세금계산서 (Tax)' });
    }

    return docs;
  };

  // ... (UI Helpers 기존 유지) ...
  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case '발주완료':
      case 'ORDERED':
        return 'bg-blue-100 text-blue-800';
      case '역발주':
      case '업체확인':
      case 'REVERSE_ORDERED':
        return 'bg-purple-100 text-purple-800';
      case '청구서도착':
      case 'INVOICED':
        return 'bg-indigo-100 text-indigo-800';
      case '정산완료':
      case 'SETTLED':
        return 'bg-green-100 text-green-800';
      case '세금계산서':
      case '세금계산서발행완료':
      case 'TAX_ISSUED':
        return 'bg-orange-100 text-orange-800';
      case '거래완료':
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800';
      case '취소':
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleVendorSelect = (vendor: any) => {
    const normalizedVendor: Vendor = {
      vendorIdx: Number(vendor.vendorIdx),
      name: vendor.name || '',
      category: typeof vendor.category === 'string' ? vendor.category : (vendor.category?.name || ''),
      subCategory: vendor.subCategory || '',
      contact: vendor.contact || '',
      managerName: vendor.managerName || vendor.manager || '',
      email: vendor.email || '',
      commissionRate: Number(vendor.commissionRate || 0),
      vendorType: vendor.vendorType,
      contextType: vendor.contextType,
      relationshipType: vendor.relationshipType,
      ownerVendorIdx: Number.isFinite(Number(vendor.ownerVendorIdx)) ? Number(vendor.ownerVendorIdx) : null,
      hostVendorIdx: Number.isFinite(Number(vendor.hostVendorIdx)) ? Number(vendor.hostVendorIdx) : null,
      isOwnerVendor: normalizeBoolean(vendor.isOwnerVendor),
      isPartnerVendor: normalizeBoolean(vendor.isPartnerVendor),
      isPartnershipVendor: normalizeBoolean(vendor.isPartnershipVendor),
      isGuestContext: normalizeBoolean(vendor.isGuestContext)
    };
    setSelectedVendor(normalizedVendor);
    setOrderFormData((prev) => ({
      ...prev,
      vendor: normalizedVendor.name,
      vendorManager: normalizedVendor.managerName,
      vendorPhone: normalizedVendor.contact,
      vendorFax: ''
    }));
    setShowVendorSelect(false);
  };

  const formatNumber = (val: string | number) => {
    const num = typeof val === 'string' ? parseInt(val.replace(/[^0-9]/g, '') || '0') : val;
    return num.toLocaleString();
  };


  return (
    <div>
      {/* ... 상단 헤더 생략 ... */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-800">발주내역</h3>
        <button
          onClick={() => {
            const hasRehearsal = Boolean(customer.rehearsalDateTime && customer.rehearsalDateTime !== '-');
            const hasWedding = Boolean(customer.weddingDateTime && customer.weddingDateTime !== '-');
            if (!hasRehearsal || !hasWedding) {
              alert('발주서를 생성하려면 고객의 촬영일과 본식일이 모두 등록되어 있어야 합니다.');
              return;
            }
            setShowOrderForm(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm flex items-center gap-2"
        >
          <i className="ri-add-line"></i> 발주 등록
        </button>
      </div>

      <div className="space-y-4">
        {customerOrders.map((order) => (
          <div 
            key={order.id} 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer relative" // relative 추가
            onClick={() => { setSelectedOrder(order); setShowOrderDetailModal(true); }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                 <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                   <i className="ri-file-text-line text-blue-600 text-xl"></i>
                 </div>
                 <div>
                   <h4 className="font-semibold text-gray-900">ORD-{order.id}</h4>
                   <p className="text-sm text-gray-600">{order.vendor} • {order.service}</p>
                 </div>
              </div>
              
              <div className="flex items-center gap-2">
                {order.isExternalPartner && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 cursor-help"
                    title="웨드민에 가입하지 않은 업체입니다. 역발주/인보이스/세금계산서를 플래너가 대리 처리합니다.">
                    미초대 업체
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getOrderStatusColor(order.status)}`}>
                  {order.status}
                </span>

                {/* ✅ [수정됨] 다운로드 드롭다운 메뉴 */}
                <div className="relative">
                  <button
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setActiveDownloadMenuId(activeDownloadMenuId === order.id ? null : order.id);
                    }}
                    className={`p-1.5 rounded-full transition-colors ${
                      activeDownloadMenuId === order.id ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title="문서 다운로드"
                  >
                    <i className="ri-download-line text-lg"></i>
                  </button>

                  {/* 드롭다운 본문 */}
                  {activeDownloadMenuId === order.id && (
                    <div 
                      ref={menuRef}
                      className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
                      onClick={(e) => e.stopPropagation()} 
                    >
                      <div className="py-1">
                        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500">
                          다운로드 가능한 문서
                        </div>
                        {getDownloadableDocs(order).map((doc) => (
                          <button
                            key={doc.type}
                            onClick={() => handleDownloadPdf(order.id, doc.type)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-between group"
                          >
                            <span>{doc.label}</span>
                            <i className="ri-file-download-line opacity-0 group-hover:opacity-100 text-blue-500"></i>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* -- End Dropdown -- */}

              </div>
            </div>

            {/* ... Progress Bar & Info Grid (기존 유지) ... */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">진행률</span>
                <span className="text-sm font-medium text-gray-900">{order.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${order.progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400 px-1">
                <span>발주</span>
                <span>역발주</span>
                <span>청구</span>
                <span>정산</span>
                <span>계산서</span>
                <span>완료</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 bg-gray-50 p-4 rounded-lg">
               {/* ... */}
                <div>
                <p className="text-xs text-gray-500 mb-1">담당자</p>
                <p className="text-sm font-medium text-gray-900">{order.manager}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">계약금액</p>
                <p className="text-sm font-medium text-gray-900">{formatNumber(order.amount)}원</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">결제금액</p>
                <p className="text-sm font-medium text-green-600">{formatNumber(order.paidAmount)}원</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">발주일</p>
                <p className="text-sm font-medium text-gray-900">{order.orderDate}</p>
              </div>
            </div>

            {/* Action Buttons (기존 유지) */}
            <div 
              className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              {cancellableStatuses.includes(order.status) && (
                  <button
                  onClick={() => handleCancelOrder(order.id)}
                  className="px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-sm rounded-md transition-colors border border-red-200"
                >
                   발주취소
                 </button>
              )}

              {/* 단방향 발주: 대리 역발주 확정 */}
              {order.isExternalPartner && order.status === '발주완료' && (
                <button
                  onClick={() => { setSelectedOrder(order); setShowReverseOrderModal(true); }}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors shadow-sm flex items-center gap-1"
                >
                  <i className="ri-check-line"></i> 대리 역발주 확정
                </button>
              )}

              {/* 단방향 발주: 대리 인보이스 등록 */}
              {order.isExternalPartner && order.status === '업체확인' && (
                <button
                  onClick={() => { setSelectedOrder(order); setShowInvoiceModal(true); }}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors shadow-sm flex items-center gap-1"
                >
                  <i className="ri-file-text-line"></i> 대리 인보이스 등록
                </button>
              )}

              {/* 단방향 발주: 대리 세금계산서 등록 */}
              {order.isExternalPartner && order.status === '정산완료' && (
                <button
                  onClick={() => { setSelectedOrder(order); setShowTaxInvoiceModal(true); }}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors shadow-sm flex items-center gap-1"
                >
                  <i className="ri-file-paper-2-line"></i> 대리 세금계산서 등록
                </button>
              )}

               {/* ... 기존 버튼 로직들 그대로 복사/유지 ... */}
               {order.status === '업체확인' && (
                <>
                  {/* <button
                    onClick={() => { setSelectedOrder(order); setShowInvoiceModal(true); }}
                    className="px-3 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 text-sm rounded-md transition-colors font-medium flex items-center gap-1"
                  >
                    <i className="ri-file-list-3-line"></i> 인보이스 확인
                  </button> */}
                  <button
                    onClick={() => { setSelectedOrder(order); setShowPaymentCompleteModal(true); }}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors shadow-sm flex items-center gap-1"
                  >
                    <i className="ri-money-dollar-circle-line"></i> 정산/송금
                  </button>
                </>
              )}

              {order.status === '청구서도착' && (
                 <button
                 onClick={() => { setSelectedOrder(order); setShowPaymentCompleteModal(true); }}
                 className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors shadow-sm flex items-center gap-1"
               >
                 <i className="ri-money-dollar-circle-line"></i> 정산/송금
               </button>
              )}

              {order.status === '정산완료' && (
                <>
                  <button
                    onClick={() => { setSelectedOrder(order); setShowTaxInvoiceModal(true); }}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-1"
                  >
                    <i className="ri-file-paper-2-line"></i> 세금계산서
                  </button>
                  <button
                   onClick={() => handleTransactionComplete(order.id)}
                   className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900 transition-colors shadow-sm flex items-center gap-1"
                 >
                   <i className="ri-check-double-line"></i> 거래완료
                 </button>
                </>
              )}
              
              <button 
                onClick={() => { setSelectedOrder(order); setShowOrderDetailModal(true); }}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full text-gray-500 transition-colors ml-2"
                title="상세보기"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          </div>
        ))}

        {customerOrders.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
            <p className="text-gray-500">등록된 발주 내역이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 모달들 (기존 유지) */}
      <OrderFormModal
        isOpen={showOrderForm}
        onClose={() => { setShowOrderForm(false); setSelectedVendor(null); }}
        formData={orderFormData}
        setFormData={setOrderFormData}
        customer={customer}
        customerId={customerId}
        selectedVendor={selectedVendor}
        onVendorSelectClick={() => setShowVendorSelect(true)}
        onVendorRemoveClick={() => {
          setSelectedVendor(null);
          setOrderFormData((prev) => ({ ...prev, vendor: '', vendorManager: '', vendorPhone: '' }));
        }}
        onSubmit={handleAddOrder}
      />
      <VendorSelectModal
        isOpen={showVendorSelect}
        onClose={() => { setShowVendorSelect(false); }}
        onSelect={handleVendorSelect}
      />
      
      {selectedOrder && (
        <>
            <OrderDetailModal 
              isOpen={showOrderDetailModal} 
              onClose={() => { setShowOrderDetailModal(false); setSelectedOrder(null); }} 
              order={selectedOrder} 
              onVendorClick={() => navigate('/dashboard/vendors')}
              onInvoiceClick={(o) => { setSelectedOrder(o); setShowInvoiceModal(true); }} 
              onTaxInvoiceClick={(o) => { setSelectedOrder(o); setShowTaxInvoiceModal(true); }} 
              onPaymentCompleteClick={(o) => { setSelectedOrder(o); setShowPaymentCompleteModal(true); }} 
              getOrderStatusColor={getOrderStatusColor} 
            />
            <InvoiceModal isOpen={showInvoiceModal} onClose={() => setShowInvoiceModal(false)} order={selectedOrder} />
            <TaxInvoiceModal isOpen={showTaxInvoiceModal} onClose={() => setShowTaxInvoiceModal(false)} order={selectedOrder} />
            <PaymentCompleteModal 
              isOpen={showPaymentCompleteModal} 
              onClose={() => setShowPaymentCompleteModal(false)} 
              order={selectedOrder} 
              onSubmit={handleSettlement} 
            />
            <ReverseOrderModal isOpen={showReverseOrderModal} onClose={() => setShowReverseOrderModal(false)} order={selectedOrder} onSubmit={handleProxyConfirmOrder} />
        </>
      )}
    </div>
  );
}
